import { type HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { DOCUMENT, type Injector } from '@angular/core';
import { Router } from '@angular/router';
import type { PortalEntity, PortalSettings, UserMeEntity, UserStorageEntity } from '@sds/api/administration/models';
import {
  AdministrationApiPortalService,
  AdministrationApiUserService,
  AdministrationApiUserStorageService
} from '@sds/api/administration/services';
import type { LandingPageEntity } from '@sds/api/widget/models';
import { WidgetLandingPageService } from '@sds/api/widget/services';
import { ConfigProperty, LocaleStore, PortalSettingsStore, UserStore, type ConfigService } from '@sds/shared';
import { Theme, type Language } from '@sds/shared/enums';
import { UserSetting, UserSettingsStore } from '@sds/user-settings';
import { captureConsoleIntegration, init, setUser } from '@sentry/angular';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, firstValueFrom, forkJoin, map, of, switchMap, tap, zip, type Observable } from 'rxjs';

/**
 * potentially can include server error codes but Jens wants to have an overview about them in Sentry
 * 500 Internal Server Error|502 Bad Gateway|503 Service Unavailable
 * https://stackoverflow.com/a/61653575/5790305
 */
const serverErrorsRegex = new RegExp('401 Unauthorized|403 Forbidden|404 Not Found', 'mi');

export async function appInitializer(configService: ConfigService, injector: Injector): Promise<unknown> {
  const administrationApiUserService = injector.get(AdministrationApiUserService);
  const userStore = injector.get(UserStore);
  const localeStore = injector.get(LocaleStore);
  const router = injector.get(Router);
  const userStorageService = injector.get(AdministrationApiUserStorageService);
  const widgetLandingPageService = injector.get(WidgetLandingPageService);
  const userSettingsStore = injector.get(UserSettingsStore);
  const portalSettingsStore = injector.get(PortalSettingsStore);
  const portalService = injector.get(AdministrationApiPortalService);
  const document = injector.get(DOCUMENT);
  const translateService = injector.get(TranslateService);

  return firstValueFrom(
    zip([
      loadUser(administrationApiUserService, userStore, localeStore, router),
      loadUserSettings(userStorageService, widgetLandingPageService, userSettingsStore)
    ]).pipe(
      map(([user]) => user!),
      filter(user => !!user),
      switchMap(user => loadCurrentPortal(user.currentPortalId ?? '', portalService, portalSettingsStore)),
      switchMap(portal => loadPortalSettings(portal.id!, portalService, portalSettingsStore)),
      tap(() => {
        if (userStore.user()) {
          initSentry(document, configService, userStore.user()!);
        }
      }),
      switchMap(() => {
        const lang = userStore.user()?.languageId;
        return lang ? translateService.use(lang) : of(null);
      })
    ),
    { defaultValue: null }
  );
}

function loadUser(
  administrationApiUserService: AdministrationApiUserService,
  userStore: UserStore,
  localeStore: LocaleStore,
  router: Router
): Observable<UserMeEntity | null> {
  return administrationApiUserService.getMeUserItem({ id: 'me' }).pipe(
    map(user => user as UserMeEntity),
    map(user => {
      user.languageId = user.languageId.replace('_', '-') as Language;
      return user;
    }),
    tap(user => {
      userStore.setUser(user);
      localeStore.setLocale(user.languageId as Language);
    }),
    catchError(error => {
      router.navigate(['oauth-error'], { queryParams: { message: error?.message, status: error?.status } });
      return of(null);
    })
  );
}

function loadUserSettings(
  userStorageService: AdministrationApiUserStorageService,
  WidgetLandingPageService: WidgetLandingPageService,
  userSettingsStore: UserSettingsStore
): Observable<UserStorageEntity[]> {
  return forkJoin([
    userStorageService.getUserStorageCollection().pipe(catchError(() => of([] as UserStorageEntity[]))),
    WidgetLandingPageService.getLandingPageCollection().pipe(catchError(() => of([] as LandingPageEntity[])))
  ]).pipe(
    map(([settings, landingPage]) => {
      if (landingPage.length) {
        const { id, pinboardId } = landingPage[0];
        if (pinboardId) {
          settings.push({
            id,
            name: 'landingPage',
            type: 'landingPage',
            values: [pinboardId]
          });
        }
      }
      return settings;
    }),
    tap(settings => {
      const temperatureItem: UserStorageEntity | undefined = settings.find(s => s.type === UserSetting.TemperatureUnit);
      const distanceItem: UserStorageEntity | undefined = settings.find(s => s.type === UserSetting.DistanceUnit);
      const landingPageItem: UserStorageEntity | undefined = settings.find(s => s.type === UserSetting.LandingPage);
      const landingPage: LandingPageEntity | null = landingPageItem
        ? {
            id: landingPageItem.id,
            pinboardId: landingPageItem.values[0] //the pinboard id is always the first value of the array.
          }
        : null;
      const themeItem: UserStorageEntity | undefined = settings.find(s => s.type === UserSetting.Theme);
      const theme =
        themeItem?.name && Object.values(Theme).includes(themeItem?.name as Theme)
          ? themeItem
          : userSettingsStore.themeEntity();
      const analysisFilters: UserStorageEntity | undefined =
        settings.find(s => s.type === UserSetting.AnalysisOverviewFilter) ?? userSettingsStore.analysisFiltersEntity();
      userSettingsStore.setLandingPageEntity(landingPage);
      userSettingsStore.setThemeEntity(theme);
      userSettingsStore.setAnalysisFiltersEntity(analysisFilters);

      if (temperatureItem) {
        userSettingsStore.setTemperatureUnitEntity(temperatureItem);
      }
      if (distanceItem) {
        userSettingsStore.setDistanceUnitEntity(distanceItem);
      }
    })
  );
}

function loadCurrentPortal(
  id: string,
  portalService: AdministrationApiPortalService,
  portalSettingsStore: PortalSettingsStore
): Observable<PortalEntity> {
  return portalService.getPortalItem({ id }).pipe(
    tap(portal => {
      portalSettingsStore.setPortal(portal);
    })
  );
}

function loadPortalSettings(
  id: string,
  portalService: AdministrationApiPortalService,
  portalSettingsStore: PortalSettingsStore
): Observable<PortalSettings | null> {
  return portalService.getPortalSettingsPortalItem({ id }).pipe(
    map(settings => settings as PortalSettings),
    tap(settings => {
      portalSettingsStore.setPortalSettings(settings);
    }),
    catchError(() => of(null))
  );
}

function initSentry(document: Document, configService: ConfigService, user: UserMeEntity): void {
  const sentryUrl: string | null = configService.get(ConfigProperty.SentryDns);
  if (sentryUrl?.length) {
    setUser({ userId: user.id, email: user.primaryMail, username: user.displayName });
    init({
      attachStacktrace: true,
      dsn: sentryUrl,
      enabled:
        !document.location.origin.includes('localhost') && !document.location.origin.includes('ta.enerest.world'),
      environment: document.location.hostname,
      release: `enerest-v4@${configService.get(ConfigProperty.VersionNumber) ?? '0.0.0'}`,
      ignoreTransactions: ['/print/report/:reportId/'],
      ignoreErrors: [serverErrorsRegex],
      integrations: [captureConsoleIntegration()],
      beforeSend: (event, hint) => {
        const response = hint.originalException as HttpErrorResponse;

        if (
          response &&
          response.status &&
          [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden, HttpStatusCode.NotFound].includes(response.status)
        ) {
          return null;
        }
        return event;
      }
    });
  }
}
